/**
 * Booking Cart Validator
 *
 * CRITICAL RULE: This file MUST NEVER block, intercept, or preventDefault
 * on the checkout button. Checkout is sacred — it must always work even if
 * every line of code here fails. Server-side validation at order creation
 * catches any race conditions. Client-side logic here is purely for UX
 * (removing expired reservations from the cart display before the user
 * tries to check out).
 *
 * Uses the `expires_at` property from line items (server-set at reservation
 * time, re-rendered by Liquid on every page load). No proxy call required.
 */

(function () {
  function init() {
    const items = (window.bookingLineItems || []).slice();
    if (items.length === 0) return;

    cleanupExpired(items);
    scheduleNextCheck(items);

    // Re-check when user returns to the tab (e.g. left it open over lunch)
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') cleanupExpired(items);
    });

    // Re-check when restored from bfcache (back/forward navigation)
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) cleanupExpired(items);
    });
  }

  async function cleanupExpired(items) {
    const now = Date.now();
    const expired = items.filter(function (item) {
      return item.expires_at && new Date(item.expires_at).getTime() <= now;
    });

    if (expired.length === 0) return;

    for (const item of expired) {
      await removeFromCart(item.key);
      const idx = items.indexOf(item);
      if (idx > -1) items.splice(idx, 1);
    }

    showExpiryNotification();
  }

  function scheduleNextCheck(items) {
    const now = Date.now();
    const upcoming = items
      .map(function (i) { return new Date(i.expires_at).getTime(); })
      .filter(function (t) { return t > now; });

    if (upcoming.length === 0) return;

    // Fire 500ms after the earliest expiration to account for clock skew
    const delay = Math.min.apply(null, upcoming) - now + 500;

    setTimeout(function () {
      cleanupExpired(items).then(function () { scheduleNextCheck(items); });
    }, Math.max(1000, delay));
  }

  async function removeFromCart(lineItemKey) {
    try {
      await fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineItemKey, quantity: 0 })
      });

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'booking-cart-validator' });
      }
    } catch (err) {
      console.error('Failed to remove expired booking from cart:', err);
    }
  }

  function showExpiryNotification() {
    const strings = window.bookingStrings || {};
    const message = strings.expired_removed || 'A booking reservation has expired and was removed from your cart.';
    if (window.MontisNotification) {
      window.MontisNotification.show({
        message: message,
        type: 'warning',
        duration: 6000,
        dismissible: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
